import { native } from '@forest/native-services'
import { useStore } from '@nanostores/solid'
import * as Sentry from '@sentry/solid'
import { useQueryClient } from '@tanstack/solid-query'
import { isHTTPError } from 'ky'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
} from 'solid-js'
import {
  cakeCollection,
  coinBoxProductId,
  eventEndDate,
  eventStartDate,
  gemBoxProductId,
} from '~/config'
import { useEffectiveNow, useEventEnded } from '~/hooks/use-event-ended'
import { deriveDayCards, type DayCard } from '~/lib/derive-day-cards'
import { deriveExtraBoxes, type ExtraBoxOption } from '~/lib/derive-extra-boxes'
import { deriveRewardPresentation } from '~/lib/derive-reward-presentation'
import type { ShopItem } from '~/lib/derive-shop-catalog'
import { isConnectivityError } from '~/lib/is-connectivity-error'
import type { DrawResult } from '~/lib/reward-types'
import { m } from '~/paraglide/messages'
import {
  adjustActivityTokenBalance,
  useActivityTokenBalance,
} from '~/resources/activity-token-balance'
import { useClaimReward } from '~/resources/claim-reward'
import { useExtraBoxProduct } from '~/resources/extra-box-product'
import { useLotteryDraw } from '~/resources/lottery-draw'
import {
  selectIsTrialEligible,
  type PaywallBootstrap,
} from '~/resources/paywall-bootstrap'
import { useAllBoostingPacksPurchased } from '~/resources/boosting-pack-stats'
import {
  productPurchaseStatsQueryKey,
  useProductPurchaseStats,
} from '~/resources/product-purchase-stats'
import { useProducts } from '~/resources/products'
import { useProgresses } from '~/resources/progresses'
import { PurchaseFailure, usePurchase } from '~/resources/purchase'
import { useSubscribeMutation } from '~/resources/subscribe'
import { useUnlockedTrees } from '~/resources/unlocked-trees'
import { $isBoostingBannerDismissed } from '~/store/boosting-banner'
import { $isBoostingPacksOpen } from '~/store/boosting-packs'
import { $debugForceEnded } from '~/store/debug-force-ended'
import { showDialog } from '~/store/dialog'
import {
  dispatchGenericError,
  handleStaleRewardError,
  showDataLoadFailDialog,
} from '~/store/error-dialog'
import { showInsufficientBalanceDialog } from '~/store/insufficient-balance-dialog'
import { markPageRevealed } from '~/store/page-revealed'
import {
  $isPlusPaywallOpen,
  closePlusPaywall,
  openPlusPaywall,
} from '~/store/plus-paywall'
import { $isRulesDialogOpen, closeRulesDialog } from '~/store/rules-dialog'
import { $isInSubscription } from '~/store/service-level'
import {
  $isSubscribedViewOpen,
  closeSubscribedView,
  openSubscribedView,
} from '~/store/subscribed-view'
import { closeTrialTimelineView } from '~/store/trial-timeline-view'
import { BoostingPackRewardQueue } from './boosting-pack-reward-queue'
import { BoostingPacksBanner } from './boosting-packs-banner'
import { BoostingPacksSheet } from './boosting-packs-sheet'
import { BubbleReminder } from './bubble-reminder'
import { CollectionCabinet } from './collection-cabinet'
import { EventEndedPanel } from './event-ended-panel'
import { ExchangeShop } from './exchange-shop'
import { ExtraBoxOpenConfirmDialog } from './extra-box-open-confirm-dialog'
import { GrantPermissionDialog } from './grant-permission-dialog'
import { HeroBackground, HeroSection } from './hero-section'
import { OtherCard } from './other-card'
import { PlusPaywall, type PaywallEntry } from './plus-paywall'
import { PlusTreeRewardPage } from './plus-tree-reward-page'
import { RewardPage } from './reward-page'
import { RulesDialog } from './rules-dialog'
import { ShopOverlay } from './shop-overlay'
import { SubscribedView } from './subscribed-view'
import { TabBar, type TabValue } from './tab-bar'
import { TodayCard } from './today-card'
import { TopNav } from './top-nav'

const COLLECTION_HIGHLIGHT_KEY = 'birthday_2026_collection_highlight_played'

const cakeCollectionTreeIds = new Set(cakeCollection.map((c) => c.treeTypeId))

function hasPlayedCollectionHighlight() {
  try {
    return localStorage.getItem(COLLECTION_HIGHLIGHT_KEY) === '1'
  } catch {
    return false
  }
}

function markCollectionHighlightPlayed() {
  try {
    localStorage.setItem(COLLECTION_HIGHLIGHT_KEY, '1')
  } catch {
    // localStorage may be unavailable (private mode, WebView restrictions);
    // the cue will simply replay until the flag can be persisted.
  }
}

type RewardDrawState = { draw: DrawResult; isNewCollectionTree: boolean }
type ShopRewardState = {
  treeGid: number
  numRewardType: 1 | 2
  isNewCollectionTree: boolean
}

const SPECIAL_TREE_GID = 169

export function EventHome(props: { started?: boolean }) {
  // Progressive disclosure cadence (300ms steps after `started`):
  //   bg + Hero bg (0ms) → hero title (300ms) → hero text (600ms)
  //   → tabs+shop (900ms) → cards (1200ms)
  // Everything shares the same `introRise` keyframe; offset delays make the
  // page settle in waves rather than slamming in all at once.
  const introStyle = (delayMs: number) =>
    props.started
      ? {
          animation: 'introRise 0.6s ease-out both',
          'animation-delay': `${delayMs}ms`,
        }
      : undefined
  // BubbleReminder enters 2s AFTER the disclosure wave settles (cards land
  // at 1200ms + 600ms animation = ~1800ms) → fires at 3800ms post-`started`,
  // then bounces in via the `bubble-in` keyframe.
  const [showBubble, setShowBubble] = createSignal(false)
  createEffect(() => {
    if (props.started && !showBubble()) {
      const timer = window.setTimeout(() => setShowBubble(true), 3800)
      onCleanup(() => window.clearTimeout(timer))
    }
  })
  // Cards/cabinet swap on tab change. The introRise animation should only
  // play on the page's initial reveal — after the cards layer has settled
  // (delay 1200ms + 600ms duration), subsequent tab switches mount instantly.
  const [cardsRevealed, setCardsRevealed] = createSignal(false)
  createEffect(() => {
    if (props.started && !cardsRevealed()) {
      const timer = window.setTimeout(() => {
        setCardsRevealed(true)
        // Also flips the global $pageRevealed flag so ProgressArc (mounted
        // inside the still-opacity-0 cards) knows it can run its one-shot
        // intro transition now that the disclosure has settled.
        markPageRevealed()
      }, 1800)
      onCleanup(() => window.clearTimeout(timer))
    }
  })
  // EventHome only mounts after the campaign is live (gated by App), and
  // `started` only flips false → true once per session — so a plain effect
  // tracking it fires the main-view page_view exactly once.
  createEffect(() => {
    if (!props.started) return
    native.general.logEvent({
      event_name: 'page_view',
      event_params: { page_name: '2026_birthday_main' },
    })
  })
  const cardsIntroStyle = () => (cardsRevealed() ? undefined : introStyle(1200))
  const [activeTab, setActiveTab] = createSignal<TabValue>('baking')
  const [rewardDraw, setRewardDraw] = createSignal<RewardDrawState | null>(null)
  const [shopReward, setShopReward] = createSignal<ShopRewardState | null>(null)
  const [pickedExtraBox, setPickedExtraBox] = createSignal<{
    currency: 'coin' | 'gem'
    option: ExtraBoxOption
  } | null>(null)
  const [flashCollection, setFlashCollection] = createSignal(false)
  const [shopOpen, setShopOpen] = createSignal(false)
  const [paywallEntry, setPaywallEntry] =
    createSignal<PaywallEntry>('claim-box')
  const paywallOpen = useStore($isPlusPaywallOpen)
  const subscribedViewOpen = useStore($isSubscribedViewOpen)
  const rulesDialogOpen = useStore($isRulesDialogOpen)
  const boostingPacksOpen = useStore($isBoostingPacksOpen)
  const boostingBannerDismissed = useStore($isBoostingBannerDismissed)
  const isInSubscription = useStore($isInSubscription)
  const subscribeMutation = useSubscribeMutation()
  const queryClient = useQueryClient()
  // Effective clock — real server time unless the QA force-ended toggle
  // pins it past the event end (see use-event-ended.ts).
  const now = useEffectiveNow()
  const eventEnded = useEventEnded()
  const progressesQuery = useProgresses()
  const unlockedTreesQuery = useUnlockedTrees()
  const coinBoxQuery = useExtraBoxProduct(() => coinBoxProductId)
  const gemBoxQuery = useExtraBoxProduct(() => gemBoxProductId)
  const coinStatsQuery = useProductPurchaseStats(() => coinBoxProductId)
  const gemStatsQuery = useProductPurchaseStats(() => gemBoxProductId)
  const balanceQuery = useActivityTokenBalance()

  // warm-up exchange shop
  const productsQuery = useProducts()

  createEffect(() => {
    if (progressesQuery.isError) {
      showDataLoadFailDialog({
        source: 'failed_to_load_progresses',
        step: 'load_progresses',
        error: progressesQuery.error,
      })
    } else if (productsQuery.isError) {
      showDataLoadFailDialog({
        source: 'failed_to_load_products',
        step: 'load_campaign_products',
        error: productsQuery.error,
      })
    } else if (coinBoxQuery.isError) {
      showDataLoadFailDialog({
        source: 'failed_to_load_coin_box_product',
        step: 'load_coin_box_product',
        error: coinBoxQuery.error,
      })
    } else if (gemBoxQuery.isError) {
      showDataLoadFailDialog({
        source: 'failed_to_load_gem_box_product',
        step: 'load_gem_box_product',
        error: gemBoxQuery.error,
      })
    } else if (coinStatsQuery.isError) {
      showDataLoadFailDialog({
        source: 'failed_to_load_coin_purchase_stats',
        step: 'load_coin_purchase_stats',
        error: coinStatsQuery.error,
      })
    } else if (gemStatsQuery.isError) {
      showDataLoadFailDialog({
        source: 'failed_to_load_gem_purchase_stats',
        step: 'load_gem_purchase_stats',
        error: gemStatsQuery.error,
      })
    }
  })
  const claimMutation = useClaimReward()
  const purchaseMutation = usePurchase()
  const lotteryDrawMutation = useLotteryDraw()

  const extraBoxes = createMemo(() =>
    deriveExtraBoxes({
      products: [
        ...(coinBoxQuery.data ? [coinBoxQuery.data] : []),
        ...(gemBoxQuery.data ? [gemBoxQuery.data] : []),
      ],
      statsById: {
        ...(coinStatsQuery.data
          ? { [coinBoxProductId]: coinStatsQuery.data }
          : {}),
        ...(gemStatsQuery.data
          ? { [gemBoxProductId]: gemStatsQuery.data }
          : {}),
      },
      coinBoxProductId,
      gemBoxProductId,
    }),
  )

  const unlockedTreeIds = createMemo(
    () => unlockedTreesQuery.data ?? new Set<number>(),
  )

  const cards = createMemo(() =>
    deriveDayCards({
      progresses: progressesQuery.data ?? [],
      now: now(),
      eventStartDate,
      eventEndDate,
    }),
  )
  // The ended panel rides the rail as a pseudo-day one past the last card so
  // the existing data-day machinery (centering, scrollToDay, the mount
  // auto-jump to the last item) picks it up without special cases.
  const endedPanelDay = () => cards().length + 1

  let railEl: HTMLDivElement | undefined
  // The card whose center is closest to the rail's viewport center — used to
  // gate the BubbleReminder so it only shows while today's card is in view.
  const [centeredDay, setCenteredDay] = createSignal<number | null>(null)

  const updateCenteredDay = () => {
    if (!railEl) return
    const cardEls = railEl.querySelectorAll<HTMLElement>('[data-day]')
    const railCenter = railEl.scrollLeft + railEl.clientWidth / 2
    let closestDay: number | null = null
    let minDist = Infinity
    cardEls.forEach((card) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2
      const dist = Math.abs(cardCenter - railCenter)
      if (dist < minDist) {
        minDist = dist
        closestDay = Number(card.dataset.day)
      }
    })
    if (closestDay !== null) setCenteredDay(closestDay)
  }

  const setRailRef = (el: HTMLDivElement | null) => {
    if (!el) return
    railEl = el
    requestAnimationFrame(() => {
      let target = el.querySelector<HTMLElement>('[data-today="true"]')
      if (!target && eventEnded()) {
        const all = el.querySelectorAll<HTMLElement>('[data-day]')
        target = all[all.length - 1] ?? null
      }
      if (!target) return
      const center =
        target.offsetLeft - (el.clientWidth - target.clientWidth) / 2
      el.scrollTo({ left: center, behavior: 'instant' as ScrollBehavior })
      updateCenteredDay()
    })
    let scrollEndTimer = 0
    el.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollEndTimer)
        scrollEndTimer = window.setTimeout(updateCenteredDay, 150)
      },
      { passive: true },
    )
  }

  const scrollToDay = (day: number) => {
    setActiveTab('baking')
    requestAnimationFrame(() => {
      if (!railEl) return
      const card = railEl.querySelector<HTMLElement>(`[data-day='${day}']`)
      if (!card) return
      const center =
        card.offsetLeft - (railEl.clientWidth - card.clientWidth) / 2
      railEl.scrollTo({ left: center, behavior: 'smooth' })
    })
  }

  // Flipping the QA force-ended toggle ON jumps straight to the ended panel
  // so the state change is visible. A real end-of-event crossing while the
  // page is open (the 30s clock refetch) deliberately doesn't scroll — only
  // the next page entry auto-jumps (spec: jump on entry, don't yank mid-use).
  const debugForceEnded = useStore($debugForceEnded)
  createEffect(
    on(
      debugForceEnded,
      (forced, prev) => {
        if (forced && !prev) scrollToDay(endedPanelDay())
      },
      { defer: true },
    ),
  )

  const todayCard = createMemo(() => cards().find((c) => c.isToday))
  // Claimable-but-unclaimed buttons strictly BEFORE the currently visible
  // card. The plus tier only counts for subscribers — non-subscribers see an
  // upgrade CTA there, not a claim button. Tapping the bubble snaps to the
  // nearest unclaimed day; claiming (and the centered card shifting) drains
  // the count. Reaches 0 → bubble hides.
  const claimableButtons = (c: DayCard) =>
    (c.free.state === 'finish' ? 1 : 0) +
    (isInSubscription() && c.plus.state === 'finish' ? 1 : 0)
  const unclaimedDaysBefore = createMemo(() => {
    const centered = centeredDay()
    if (centered === null) return []
    return cards()
      .filter((c) => c.day < centered && claimableButtons(c) > 0)
      .map((c) => c.day)
  })
  const unclaimedButtonCount = createMemo(() => {
    const centered = centeredDay()
    if (centered === null) return 0
    return cards()
      .filter((c) => c.day < centered)
      .reduce((sum, c) => sum + claimableButtons(c), 0)
  })
  const nearestUnclaimedDay = createMemo(() => {
    const days = unclaimedDaysBefore()
    return days.length > 0 ? days[days.length - 1] : null
  })
  // Picks the ended panel's copy: any claimable box left anywhere in the
  // event → "your cake boxes are waiting", otherwise the plain wrap-up.
  const hasUnclaimedBoxes = createMemo(() =>
    cards().some((c) => claimableButtons(c) > 0),
  )

  const claimingProgressId = (): number | null =>
    claimMutation.isPending ? (claimMutation.variables ?? null) : null

  const claimRetryCounts = new Map<number, number>()

  const handleClaim = (progressId: number) => {
    // Guard against double-taps racing ahead of the reactive disabled state —
    // a second mutate would fire a duplicate PUT for the same progress.
    if (claimMutation.isPending) return
    const previousUnlockedTreeIds = unlockedTreeIds()
    claimMutation.mutate(progressId, {
      onSuccess: (response) => {
        claimRetryCounts.delete(progressId)
        const draw = response.draw_results?.[0]
        if (draw) {
          const presentation = deriveRewardPresentation(draw)
          const isNewCollectionTree =
            !presentation.isDuplicate &&
            cakeCollectionTreeIds.has(presentation.treeGid) &&
            !previousUnlockedTreeIds.has(presentation.treeGid)
          setRewardDraw({ draw, isNewCollectionTree })
        }
      },
      onError: (error) => {
        const retryCount = claimRetryCounts.get(progressId) ?? 0
        claimRetryCounts.set(progressId, retryCount + 1)
        // 4xx on .../reward typically means the local progress is stale;
        // refetch and dismiss silently the first time. Persistent failures
        // fall through to the standard ladder so the user isn't stranded.
        if (
          isHTTPError(error) &&
          error.response.status >= 400 &&
          error.response.status < 500
        ) {
          handleStaleRewardError({
            retryCount,
            onRetry: () => handleClaim(progressId),
          })
          return
        }
        dispatchGenericError(error, () => handleClaim(progressId))
      },
    })
  }

  // Fetched eagerly (not just inside the sheet) so the insufficient-balance
  // dialogs already know whether there is still a pack left to upsell.
  const allPacksPurchased = useAllBoostingPacksPurchased()

  const fireExtraBoxPurchase = (
    currency: 'coin' | 'gem',
    option: ExtraBoxOption,
  ): Promise<void> => {
    const previousUnlockedTreeIds = unlockedTreeIds()
    return new Promise<void>((resolve) => {
      lotteryDrawMutation.mutate(
        { productId: option.productId, quantity: 1 },
        {
          onSuccess: (response) => {
            const draw = response.results[0]?.draw_results?.[0]
            if (draw) {
              const presentation = deriveRewardPresentation(draw)
              const isNewCollectionTree =
                !presentation.isDuplicate &&
                cakeCollectionTreeIds.has(presentation.treeGid) &&
                !previousUnlockedTreeIds.has(presentation.treeGid)
              setRewardDraw({ draw, isNewCollectionTree })
            }
            resolve()
          },
          onError: (error) => {
            if (
              error instanceof PurchaseFailure &&
              error.cause.kind === 'insufficient-currency'
            ) {
              showInsufficientBalanceDialog({
                currency,
                allPacksPurchased: allPacksPurchased(),
              })
              resolve()
              return
            }
            if (
              error instanceof PurchaseFailure &&
              error.cause.kind === 'daily-limit'
            ) {
              // The button should already be disabled at the limit, so a 409
              // means the cached today_count drifted from the server (e.g. a
              // draw that timed out client-side but landed). Refetch the
              // stats so `remaining` resyncs and the button disables — and
              // don't offer a retry that would just 409 again.
              queryClient.invalidateQueries({
                queryKey: productPurchaseStatsQueryKey,
              })
              showDialog({
                title: m.Limited_purchase_reached(),
                description: '',
                primaryButtonText: m.ok(),
              })
              resolve()
              return
            }
            if (!isConnectivityError(error)) {
              Sentry.captureException(error)
            }
            dispatchGenericError(error, () =>
              fireExtraBoxPurchase(currency, option),
            )
            resolve()
          },
        },
      )
    })
  }

  const handlePickExtraBox = (
    currency: 'coin' | 'gem',
    option: ExtraBoxOption,
  ) => {
    setPickedExtraBox({ currency, option })
  }

  // `onSettled` dismisses the redeem-confirm dialog on every outcome —
  // success, insufficient stamps, or error — so follow-up surfaces (reward
  // page, insufficient-balance dialog, error dialog) never stack on top of
  // a stale confirmation.
  const fireShopRedeem = (item: ShopItem, onSettled: () => void) => {
    const balance = balanceQuery.data?.balance
    if (item.price !== null && balance !== undefined && balance < item.price) {
      onSettled()
      showInsufficientBalanceDialog({
        currency: 'stamp',
        allPacksPurchased: allPacksPurchased(),
      })
      return
    }
    const previousUnlockedTreeIds = unlockedTreeIds()
    purchaseMutation.mutate(
      { productId: item.productId, quantity: 1 },
      {
        onSuccess: () => {
          if (item.price !== null) {
            adjustActivityTokenBalance(queryClient, -item.price)
          }
          onSettled()
          setShopReward({
            treeGid: item.treeTypeId,
            numRewardType: item.treeTypeId === SPECIAL_TREE_GID ? 2 : 1,
            isNewCollectionTree:
              cakeCollectionTreeIds.has(item.treeTypeId) &&
              !previousUnlockedTreeIds.has(item.treeTypeId),
          })
        },
        onError: (error) => {
          onSettled()
          if (
            error instanceof PurchaseFailure &&
            error.cause.kind === 'insufficient-currency'
          ) {
            showInsufficientBalanceDialog({
              currency: 'stamp',
              allPacksPurchased: allPacksPurchased(),
            })
            return
          }
          // Retry restarts the whole redeem; the confirmation is already
          // dismissed, so it settles into the same follow-up surfaces.
          dispatchGenericError(error, () => fireShopRedeem(item, onSettled))
        },
      },
    )
  }

  const handleClaimUpgrade = () => {
    setPaywallEntry('claim-box')
    openPlusPaywall()
  }

  const handleShopUpgrade = () => {
    setPaywallEntry('shop-stamp')
    openPlusPaywall()
  }

  const handleSubscribe = (bootstrap: PaywallBootstrap) => {
    subscribeMutation.mutate(
      {
        products: bootstrap.products,
        receiptSystemAccountId: bootstrap.receiptSystemAccountId,
        isTrialEligible: selectIsTrialEligible(bootstrap),
      },
      {
        onSuccess: () => {
          openSubscribedView()
        },
      },
    )
  }

  const handleSubscribedDone = () => {
    closeSubscribedView()
    closeTrialTimelineView()
    closePlusPaywall()
  }

  const triggerCollectionHighlight = () => {
    if (activeTab() === 'collection') return
    if (hasPlayedCollectionHighlight()) return
    markCollectionHighlightPlayed()
    // Stay flashing until the user actually taps the Collection tab — that
    // setActiveTab handler clears the flag. Matches the prototype's
    // attention-pulling pattern instead of a fixed-duration blink.
    setFlashCollection(true)
  }

  const handleRewardClose = () => {
    const current = rewardDraw()
    setRewardDraw(null)
    if (current?.isNewCollectionTree === true) triggerCollectionHighlight()
  }

  const handleShopRewardClose = () => {
    const current = shopReward()
    setShopReward(null)
    if (current?.isNewCollectionTree === true) triggerCollectionHighlight()
  }

  return (
    <main class="relative flex min-h-screen w-full flex-col items-center gap-[150px] overflow-hidden bg-gradient-to-b from-bakery-bg-from from-[54%] to-bakery-bg-to pt-[100px] text-white">
      {/* Background layer — fades in first as part of progressive disclosure */}
      <div
        class="pointer-events-none absolute inset-0 opacity-0"
        style={introStyle(0)}
      >
        <div
          aria-hidden
          class="absolute inset-x-0 top-0 h-[140px] bg-[#c6a283]"
        />
        <HeroBackground />
      </div>
      <Show when={props.started}>
        <div
          class="fixed inset-x-0 top-0 z-50 transition-opacity duration-300"
          classList={{
            'opacity-100': !shopOpen(),
            'opacity-0 [&_*]:pointer-events-none': shopOpen(),
          }}
        >
          <TopNav variant="home" onOpenShop={() => setShopOpen(true)} />
        </div>
        <div
          class="fixed inset-x-0 top-0 z-50 transition-opacity duration-300"
          classList={{
            'opacity-100': shopOpen(),
            'opacity-0 [&_*]:pointer-events-none': !shopOpen(),
          }}
        >
          <TopNav variant="shop" onBack={() => setShopOpen(false)} />
        </div>
      </Show>
      <HeroSection started={props.started} />

      <section class="relative flex w-full shrink-0 flex-col items-center gap-2 pb-12">
        <div class="opacity-0" style={introStyle(900)}>
          <TabBar
            active={activeTab()}
            onChange={(v) => {
              setActiveTab(v)
              if (v === 'collection') setFlashCollection(false)
            }}
            flashCollection={flashCollection()}
            onOpenShop={() => setShopOpen(true)}
          />
        </div>

        <Show
          when={activeTab() === 'baking'}
          fallback={
            <div
              class="w-full pb-12 pt-4"
              classList={{ 'opacity-0': !cardsRevealed() }}
              style={cardsIntroStyle()}
            >
              <CollectionCabinet unlockedTreeIds={unlockedTreeIds()} />
            </div>
          }
        >
          <div
            ref={setRailRef}
            class="no-scrollbar relative w-full snap-x snap-mandatory overflow-x-auto overflow-y-visible overscroll-x-contain pb-6 pt-3 [-webkit-overflow-scrolling:touch] [scroll-behavior:smooth]"
            classList={{ 'opacity-0': !cardsRevealed() }}
            style={cardsIntroStyle()}
          >
            {/* min-w-max makes this block wrap its overflowing children, so
                the trailing px-[...] actually pads AFTER the last item —
                without it maxScroll stops short and the ended panel (the
                only item that's ever last-and-centered) rests off-center;
                snap-center can't reach past maxScroll either. Percentage
                padding still resolves against the rail, not max-content. */}
            <div class="flex min-w-max items-start gap-2 px-[max(16px,calc((100%-clamp(280px,calc(100vw-72px),350px))/2))]">
              <For each={cards()}>
                {(card) => (
                  <div
                    data-day={card.day}
                    data-today={card.isToday ? 'true' : undefined}
                    class="shrink-0 snap-center snap-always"
                  >
                    <Show
                      when={card.isToday}
                      fallback={
                        <OtherCard
                          card={card}
                          claimingProgressId={claimingProgressId()}
                          onClaim={handleClaim}
                          onUpgrade={handleClaimUpgrade}
                          onReturnToToday={() => {
                            // After the event ends there's no today card —
                            // the button leads to the ended panel instead.
                            if (eventEnded()) {
                              scrollToDay(endedPanelDay())
                              return
                            }
                            const today = todayCard()
                            if (today) scrollToDay(today.day)
                          }}
                        />
                      }
                    >
                      <TodayCard
                        card={card}
                        claimingProgressId={claimingProgressId()}
                        extraBoxes={extraBoxes()}
                        onClaim={handleClaim}
                        onUpgrade={handleClaimUpgrade}
                        onPickExtraBox={handlePickExtraBox}
                      />
                    </Show>
                  </div>
                )}
              </For>
              <Show when={eventEnded()}>
                <div
                  data-day={endedPanelDay()}
                  class="shrink-0 snap-center snap-always"
                >
                  <EventEndedPanel hasUnclaimedBoxes={hasUnclaimedBoxes()} />
                </div>
              </Show>
            </div>
          </div>
        </Show>

        <Show
          when={
            showBubble() &&
            activeTab() === 'baking' &&
            unclaimedButtonCount() > 0 &&
            // Don't stack the reminder under the Boosting Packs banner; it
            // returns once the banner is dismissed (opened or closed away).
            boostingBannerDismissed() &&
            // Also hide while the Boosting Packs sheet is open so the bubble
            // doesn't flash through the translucent overlay when the banner
            // tap dismisses the banner and immediately opens the sheet.
            !boostingPacksOpen()
          }
        >
          <BubbleReminder
            count={unclaimedButtonCount()}
            onClick={() => {
              const day = nearestUnclaimedDay()
              if (day !== null) scrollToDay(day)
            }}
          />
        </Show>
      </section>

      <ShopOverlay open={shopOpen()}>
        <ExchangeShop
          unlockedTreeIds={unlockedTreeIds()}
          isRedeemPending={purchaseMutation.isPending}
          onRedeem={fireShopRedeem}
          onUpgrade={handleShopUpgrade}
        />
      </ShopOverlay>

      <Show when={rewardDraw()}>
        {(state) => (
          <RewardPage draw={state().draw} onClaim={handleRewardClose} />
        )}
      </Show>

      <Show when={shopReward()}>
        {(state) => (
          <PlusTreeRewardPage
            treeGid={state().treeGid}
            numRewardType={state().numRewardType}
            onClaim={handleShopRewardClose}
          />
        )}
      </Show>

      <Show when={pickedExtraBox()}>
        {(state) => (
          <ExtraBoxOpenConfirmDialog
            currency={state().currency}
            option={state().option}
            onConfirm={async () => {
              await fireExtraBoxPurchase(state().currency, state().option)
              setPickedExtraBox(null)
            }}
            onCancel={() => setPickedExtraBox(null)}
          />
        )}
      </Show>

      <Show when={paywallOpen()}>
        <PlusPaywall
          entry={paywallEntry()}
          isPending={subscribeMutation.isPending}
          onSubscribe={handleSubscribe}
          onClose={() => closePlusPaywall()}
        />
      </Show>

      <Show when={subscribedViewOpen()}>
        <SubscribedView onDone={handleSubscribedDone} />
      </Show>

      <RulesDialog open={rulesDialogOpen()} onClose={closeRulesDialog} />

      <GrantPermissionDialog />

      <Show when={props.started}>
        <BoostingPacksBanner />
      </Show>
      <BoostingPacksSheet />
      <BoostingPackRewardQueue />
    </main>
  )
}
